import { ProvidersList as ProvidersListUI } from '@openx/components-provider/lib/components/ProvidersList/ProvidersList';
import type { ProvidersListFilters } from '@openx/components-provider/lib/components/types';
import { useProviderContext } from '@openx/components-provider/lib/utils/context/ProviderContext';
import { serverSideCriteriaProcessing } from '@openx/components-table/serverSideCriteriaProcessing';
import type { CriteriaChange, CriteriaDimension } from '@openx/types/tableCriteria';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo } from 'react';
import { useHandleListCriteriaOnCompleted } from '../../../hooks/useHandleListCriteriaOnCompleted';
import { useListCriteria } from '../../../utils/useListCriteria';
import { defaultCriteria } from '../constants';
import { adjustCriteriaFilters } from './utils/adjustCriteriaFilters';

export const ProvidersList = () => {
  const { criteria, setCriteria } = useListCriteria('providers', defaultCriteria);
  const { useProvidersListFetch } = useProviderContext();
  const queryFilters = useMemo(() => adjustCriteriaFilters(criteria), [criteria]);

  const queryCrit = useMemo(
    () => serverSideCriteriaProcessing({ ...criteria, filters: queryFilters }, [defaultCriteria.sort]),
    [criteria, queryFilters]
  );

  const handleCompleted = useHandleListCriteriaOnCompleted<ProvidersListFilters>(setCriteria);

  const { error, loading, providers } = useProvidersListFetch({
    onCompleted: handleCompleted,
    variables: queryCrit,
  });

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension, ProvidersListFilters>) => {
      setCriteria(prevCriteria => {
        const newCriteriaProcessed = applyCriteriaChange(prevCriteria, newCriteria);
        if (!isEqual(newCriteriaProcessed, prevCriteria)) {
          return newCriteriaProcessed;
        }
        return prevCriteria;
      });
    },
    [setCriteria]
  );

  if (error) throw new Error('Providers Error');

  return (
    <ProvidersListUI
      criteria={criteria}
      handleCriteriaChange={handleCriteriaChange}
      loading={loading}
      providers={providers}
    />
  );
};
